/**
 * FilterChips — toggle which node KINDS are visible in the Shura graph.
 *
 * Parent owns `activeKinds` (Set<string>). null/empty Set = show all.
 * Tapping a chip toggles its kind in/out of the filter.
 */
import React from 'react';
import { Briefcase, Rocket, Users, FileText } from 'lucide-react';

const CHIPS = [
  { id: 'investor',   label: 'Investors',  icon: Briefcase, color: '#D4AF37' },
  { id: 'startup',    label: 'Startups',   icon: Rocket,    color: '#7C3AED' },
  { id: 'partner',    label: 'Partners',   icon: Users,     color: '#10B981',
    // Partner is a virtual group — maps to several actual kinds.
    matches: ['university', 'rd_center', 'service_provider', 'government', 'partner'] },
  { id: 'agreement',  label: 'Agreements', icon: FileText,  color: '#3B82F6',
    // Agreements aren't node kinds — they're edges. The chip toggles
    // the EDGE filter (parent looks at edges_filter).
    matches: ['__edge:partnership'] },
];

export default function FilterChips({ active, onToggle, counts = {}, isDark = false }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="shura-filter-chips">
      {CHIPS.map((c) => {
        const Icon = c.icon;
        const isOn = active.has(c.id);
        const count = counts[c.id] ?? null;
        return (
          <button
            key={c.id}
            data-testid={`shura-filter-${c.id}`}
            onClick={() => onToggle(c.id)}
            className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold transition-all
              ${isOn
                ? 'text-[#0A0A0A] border'
                : (isDark
                    ? 'border border-white/10 text-white/70 hover:border-white/30'
                    : 'border border-slate-200 text-slate-600 hover:border-slate-300')}`}
            style={isOn ? { backgroundColor: c.color, borderColor: c.color } : undefined}
            title={isOn ? `Hide ${c.label}` : `Show only ${c.label}`}
          >
            <Icon size={11}/> {c.label}
            {count !== null && (
              <span className={`ml-0.5 text-[9px] opacity-80 font-mono ${isOn ? 'text-[#0A0A0A]' : ''}`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Export the chip mapping so the parent can convert filter ids → node kinds.
export const FILTER_KIND_MAP = Object.fromEntries(
  CHIPS.map((c) => [c.id, c.matches || [c.id]]),
);
